import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { ArrowLeft, UserSwitch, HouseLine, User, ShieldStar } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function AdminUserDetail() {
    const { userId } = useParams();
    const nav = useNavigate();
    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-user', userId],
        queryFn: async () => (await api.get(`/admin/users/${userId}`)).data,
    });

    if (isLoading) return <div className="p-10 text-slate-500 text-sm">Loading…</div>;
    if (error || !data) return <div className="p-10 text-slate-500 text-sm">Could not load user.</div>;
    const { user: u, households } = data;

    const impersonate = async () => {
        try {
            await api.post(`/admin/impersonate/${u.user_id}`);
            toast.success(`Now viewing as ${u.name || u.email}`);
            window.location.href = '/dashboard';
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    return (
        <div className="px-6 md:px-10 py-8 max-w-6xl" data-testid="admin-user-detail-root">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
                <ShieldStar size={12} weight="duotone" />
                <Link to="/admin" className="hover:underline">All users</Link> · Detail
            </div>

            <header className="mb-6 flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    {u.picture ? <img src={u.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
                        : <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center text-xl font-semibold">{(u.name || '?').slice(0, 1)}</div>}
                    <div>
                        <h1 className="font-heading text-3xl text-slate-900 flex items-center gap-2">
                            {u.name || '(no name)'}
                            {u.is_admin && <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary px-2 py-0.5 rounded">Admin</span>}
                        </h1>
                        <div className="text-sm text-slate-600">{u.email}</div>
                        <div className="text-xs text-slate-500 mt-1">Joined {(u.created_at || '').slice(0, 10)} · user_id <span className="font-mono">{u.user_id}</span></div>
                    </div>
                </div>
                {!u.is_admin && (
                    <Button data-testid="admin-detail-impersonate" onClick={impersonate} className="rounded-full bg-primary text-primary-foreground">
                        <UserSwitch size={16} className="mr-2" /> Impersonate
                    </Button>
                )}
            </header>

            <section>
                <h2 className="font-heading text-2xl text-slate-900 mb-3">Households ({households.length})</h2>
                {households.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center text-slate-500">
                        This user has no households yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {households.map(f => (
                            <div key={f.facility_id} className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
                                <div className="flex items-center gap-2">
                                    <HouseLine size={20} className="text-primary" />
                                    <div className="font-heading text-xl text-slate-900">{f.name}</div>
                                    <span className="text-[10px] uppercase tracking-widest text-slate-500">{f.kind}</span>
                                    <span className="ml-auto text-xs text-slate-500">{f.coordinator_ids?.length || 1} coordinator{(f.coordinator_ids?.length || 1) === 1 ? '' : 's'}</span>
                                </div>
                                {(f.patients || []).length === 0 ? (
                                    <div className="mt-2 text-sm text-slate-500">No family members.</div>
                                ) : (
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {f.patients.map(p => (
                                            <div key={p.patient_id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-start gap-3">
                                                <User size={18} className="text-slate-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                                                    <div className="text-[11px] text-slate-500">
                                                        {p.dob ? `DOB ${p.dob} · ` : ''}
                                                        {p.counts.medications} meds · {p.counts.labs} labs · {p.counts.appointments} appts · {p.counts.notes} notes
                                                    </div>
                                                    {(p.allergies || []).length > 0 && <div className="text-[11px] text-red-700 mt-0.5">Allergies: {p.allergies.join(', ')}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="mt-6 text-xs text-slate-500 border-t border-slate-200 pt-4">
                To view any of these records in detail, use <button onClick={impersonate} className="text-primary underline">Impersonate</button> — a bright red banner will show at the top while you&apos;re viewing as this user, and every action is written to the audit log.
            </section>

            <div className="mt-8">
                <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                    <ArrowLeft size={14} /> Back to all users
                </Link>
            </div>
        </div>
    );
}
